import type { Event } from '@tauri-apps/api/event'
import { AxiosProgressEvent, CanceledError } from 'axios'
import { ComputedRef, computed, Ref } from 'vue'

import { draftModel } from '@/entities/messages'

import { useLocalization } from '@/shared/composables/localization'
import { usePromptServer } from '@/shared/composables/web-dav'
import { ChatFile } from '@/shared/types/files'
import { Room } from '@/shared/types/store'
import { EventProgress } from '@/shared/types/tauri'
import { tauriMode } from '@/shared/utils/import-meta'

const uploadFiles = async (files: ChatFile[], pServed?: string) => {
  const { saveFile, maxUploadFileSize } = usePromptServer()
  const { t } = useLocalization()

  const failedFilesNames: string[] = []
  const oversizedFilesNames: string[] = []

  const promises = files.map(file => new Promise<void>((resolve, reject) => {
    saveFile(file.file, file.path || '', file.name, progressEvent => {
      if (tauriMode()) {
        const progress = progressEvent as unknown as Event<EventProgress>
        file.progress.value = Math.ceil((progress.payload.transferred * 100) / progress.payload.filesize)
      } else {
        const progress = progressEvent as AxiosProgressEvent
        file.progress.value = progress.total ? Math.ceil((progress.loaded * 100) / progress.total) : 0
      }
    })
      .catch(e => {
        console.error(e)
        file.isError.value = true

        if (pServed) {
          draftModel.useDraftMessageStore().deleteDraftFile(pServed, file)
        }

        if (!(e instanceof CanceledError)) {
          failedFilesNames.push(file.file.name)

          if (file.file.size > maxUploadFileSize()) {
            oversizedFilesNames.push(file.file.name)
          }
        }
      })
      .then(() => {
        file.isLoaded.value = true

        resolve()
      })
      .catch(e => {
        console.error(e)

        if (e instanceof CanceledError) {
          resolve()

          return
        }

        file.isError.value = true

        if (pServed) {
          draftModel.useDraftMessageStore().deleteDraftFile(pServed, file)
        }

        failedFilesNames.push(file.file.name)

        if (file.file.size > maxUploadFileSize()) {
          oversizedFilesNames.push(file.file.name)
        }

        reject(e)
      })
  }))

  await Promise.all(promises)

  if (failedFilesNames.length) {
    if (oversizedFilesNames.length === failedFilesNames.length) {
      const maxFileSizeText = `${t('common.maxTypeFileSize')} - ${maxUploadFileSize() / 1024 / 1024} ${t('common.mb')}`
      throw (new Error(`${t('chat.failedToUploadFile', { files: failedFilesNames.join(',') })}. ${maxFileSizeText}`))
    }

    throw (new Error(t('chat.failedToUploadFile', { files: failedFilesNames.join(',') })))
  }
}

export const useFilesUpload = (room?: Ref<Room> | ComputedRef<Room>) => {
  const files: ComputedRef<ChatFile[]> = computed(() =>
    room
      ? draftModel.useDraftMessageStore().findByPServed(room.value.pServedUser).orElse()?.files ?? []
      : [],
  )

  return {
    files,
    uploadFiles,
  }
}
